import { Control, Util, DomUtil, Map, Canvas } from "leaflet";
import { Spinner } from "spin.js";
import { MapControl, withLeaflet } from "react-leaflet";
import PropTypes from "prop-types";

const LoadingControlElement = Control.extend({
  options: {
    delayIndicator: null,
    position: "topleft",
    separate: false,
    zoomControl: null,
    spinjs: false,
    spin: {
      lines: 7,
      length: 3,
      width: 3,
      radius: 5,
      rotate: 13,
      top: "83%"
    }
  },
  _dataLoaders: {},

  initialize(options) {
    Util.setOptions(this, options);
    this._dataLoaders = {};

    // Try to set the zoom control this control is attached to from
    // the options
    if (this.options.zoomControl !== null) {
      this.zoomControl = this.options.zoomControl;
    }
  },

  onAdd(map) {
    if (this.options.spinjs && typeof Spinner !== "function") {
      return console.error(
        "Leaflet.loading cannot load because " +
          "you didn't load spin.js (http://fgnass.github.io/spin.js/), even though you set it in options."
      );
    }
    this._addLayerListeners(map);
    this._addMapListeners(map);

    // Try to set the zoom control this control is attached to from the map
    // the control is being added to
    if (!this.options.separate && !this.zoomControl) {
      if (map.zoomControl) {
        this.zoomControl = map.zoomControl;
      } else if (map.zoomsliderControl) {
        this.zoomControl = map.zoomsliderControl;
      }
    }

    // Create the loading indicator
    let classes = "leaflet-control-loading";
    let container;
    if (this.zoomControl && !this.options.separate) {
      // If there is a zoom control, hook into the bottom of it
      container = this.zoomControl._container;
      // These classes are no longer used as of Leaflet 0.6
      classes += " leaflet-bar-part-bottom leaflet-bar-part last";

      // Loading control will be added to the zoom control. So the visible last element is not the
      // last dom element anymore. So add the part-bottom class.
      DomUtil.addClass(this._getLastControlButton(), "leaflet-bar-part-bottom");
    } else {
      // Otherwise, create a container for the indicator
      container = DomUtil.create(
        "div",
        "leaflet-control-zoom leaflet-control-layer-container leaflet-bar"
      );
    }
    // noinspection JSUnusedGlobalSymbols
    this._indicatorContainer = container;
    // noinspection JSUnusedGlobalSymbols
    this._indicator = DomUtil.create("a", classes, container);
    if (this.options.spinjs) {
      // noinspection JSUnusedGlobalSymbols
      this._spinner = new Spinner(this.options.spin).spin();
      this._indicator.appendChild(this._spinner.el);
    }
    return container;
  },

  onRemove(map) {
    this._removeLayerListeners(map);
    this._removeMapListeners(map);
  },

  remove() {
    if (!this._map) {
      return this;
    }
    if (this.zoomControl && !this.options.separate) {
      // Override Control.removeFrom() to avoid clobbering the entire
      // _container, which is the same as zoomControl's
      this._container.removeChild(this._indicator);

      if (this.onRemove) {
        this.onRemove(this._map);
      }
    } else {
      // If this control is separate from the zoomControl, call the
      // parent method so we don't leave behind an empty container
      this._map.removeControl(this);
    }

    this._map.off("unload", this.remove, this);
    this._map = null;

    return this;
  },

  addLoader(id) {
    this._dataLoaders[id] = true;
    if (this.options.delayIndicator && !this.delayIndicatorTimeout) {
      // If we are delaying showing the indicator and we're not
      // already waiting for that delay, set up a timeout.
      const that = this;
      this.delayIndicatorTimeout = setTimeout(() => {
        that.updateIndicator();
        that.delayIndicatorTimeout = null;
      }, this.options.delayIndicator);
    } else {
      // Otherwise show the indicator immediately
      this.updateIndicator();
    }
  },

  removeLoader(id) {
    delete this._dataLoaders[id];
    this.updateIndicator();

    // If removing this loader means we're in no danger of loading,
    // clear the timeout. This prevents old delays from instantly
    // triggering the indicator.
    if (
      this.options.delayIndicator &&
      this.delayIndicatorTimeout &&
      !this.isLoading()
    ) {
      clearTimeout(this.delayIndicatorTimeout);
      this.delayIndicatorTimeout = null;
    }
  },

  updateIndicator() {
    if (this.isLoading()) {
      this._showIndicator();
    } else {
      this._hideIndicator();
    }
  },

  isLoading() {
    return this._countLoaders() > 0;
  },

  _countLoaders() {
    let size = 0,
      key;
    for (key in this._dataLoaders) {
      // eslint-disable-next-line no-prototype-builtins
      if (this._dataLoaders.hasOwnProperty(key)) {
        size++;
      }
    }
    return size;
  },

  _showIndicator() {
    // Show loading indicator
    DomUtil.addClass(this._indicator, "is-loading");
    DomUtil.addClass(this._indicatorContainer, "is-loading");

    // If zoomControl exists, make the zoom-out button not last
    if (!this.options.separate) {
      if (this.zoomControl instanceof Control.Zoom) {
        DomUtil.removeClass(
          this._getLastControlButton(),
          "leaflet-bar-part-bottom"
        );
      } else if (
        typeof Control.Zoomslider === "function" &&
        this.zoomControl instanceof Control.Zoomslider
      ) {
        DomUtil.removeClass(
          this.zoomControl._ui.zoomOut,
          "leaflet-bar-part-bottom"
        );
      }
    }
  },

  _hideIndicator() {
    // Hide loading indicator
    DomUtil.removeClass(this._indicator, "is-loading");
    DomUtil.removeClass(this._indicatorContainer, "is-loading");

    // If zoomControl exists, make the zoom-out button last
    if (!this.options.separate) {
      if (this.zoomControl instanceof Control.Zoom) {
        DomUtil.addClass(
          this._getLastControlButton(),
          "leaflet-bar-part-bottom"
        );
      } else if (
        typeof Control.Zoomslider === "function" &&
        this.zoomControl instanceof Control.Zoomslider
      ) {
        DomUtil.addClass(
          this.zoomControl._ui.zoomOut,
          "leaflet-bar-part-bottom"
        );
      }
    }
  },

  _getLastControlButton() {
    const container = this.zoomControl._container;
    let index = container.children.length - 1;

    // Find the last visible control button that is not our loading
    // indicator
    while (index > 0) {
      const button = container.children[index];
      if (
        !(
          this._indicator === button ||
          button.offsetWidth === 0 ||
          button.offsetHeight === 0
        )
      ) {
        break;
      }
      index--;
    }

    return container.children[index];
  },

  _handleLoading(e) {
    this.addLoader(this.getEventId(e));
  },

  _handleBaseLayerChange(e) {
    const that = this;

    // Check for a target 'layer' that contains multiple layers, such as
    // L.LayerGroup. This will happen if you have an L.LayerGroup in an
    // L.Control.Layers.
    if (
      e.layer &&
      e.layer.eachLayer &&
      typeof e.layer.eachLayer === "function"
    ) {
      e.layer.eachLayer(layer => {
        that._handleBaseLayerChange({ layer: layer });
      });
    } else {
      // If we're changing to a canvas layer, don't handle loading
      // as canvas layers will not fire load events.
      if (!(e.layer instanceof Canvas)) {
        that._handleLoading(e);
      }
    }
  },

  _handleLoad(e) {
    this.removeLoader(this.getEventId(e));
  },

  getEventId(e) {
    if (e.id) {
      return e.id;
    } else if (e.layer) {
      return e.layer._leaflet_id;
    }
    return e.target._leaflet_id;
  },

  _layerAdd(e) {
    if (!e.layer || !e.layer.on) {
      return;
    }
    try {
      e.layer.on(
        {
          loading: this._handleLoading,
          load: this._handleLoad
        },
        this
      );
    } catch (exception) {
      console.warn(
        "L.Control.Loading: Tried and failed to add " +
          " event handlers to layer",
        e.layer
      );
      console.warn("L.Control.Loading: Full details", exception);
    }
  },

  _layerRemove(e) {
    if (!e.layer || !e.layer.off) {
      return;
    }
    try {
      e.layer.off(
        {
          loading: this._handleLoading,
          load: this._handleLoad
        },
        this
      );
    } catch (exception) {
      console.warn(
        "L.Control.Loading: Tried and failed to remove " +
          "event handlers from layer",
        e.layer
      );
      console.warn("L.Control.Loading: Full details", exception);
    }
  },

  _addLayerListeners(map) {
    // Add listeners for begin and end of load to any layers already
    // on the map
    map.eachLayer(layer => {
      if (!layer.on) {
        return;
      }
      layer.on("loading", this._handleLoading, this);
      layer.on("load", this._handleLoad, this);
    }, this);

    // When a layer is added to the map, add listeners for begin and
    // end of load
    map.on("layeradd", this._layerAdd, this);
    map.on("layerremove", this._layerRemove, this);
  },

  _removeLayerListeners(map) {
    // Remove listeners for begin and end of load from all layers
    map.eachLayer(layer => {
      if (!layer.off) {
        return;
      }
      layer.off("loading", this._handleLoading, this);
      layer.off("load", this._handleLoad, this);
    }, this);

    // Remove layeradd/layerremove listener from map
    map.off("layeradd", this._layerAdd, this);
    map.off("layerremove", this._layerRemove, this);
  },

  _addMapListeners(map) {
    // Add listeners to the map for (custom) dataloading and dataload
    // events, eg, for AJAX calls that affect the map but will not be
    // reflected in the above layer events.
    map.on("baselayerchange", this._handleBaseLayerChange, this);
    map.on("dataloading", this._handleLoading, this);
    map.on("dataload", this._handleLoad, this);
    map.on("layerremove", this._handleLoad, this);
  },

  _removeMapListeners(map) {
    map.off("baselayerchange", this._handleBaseLayerChange, this);
    map.off("dataloading", this._handleLoading, this);
    map.off("dataload", this._handleLoad, this);
    map.off("layerremove", this._handleLoad, this);
  }
});

Map.addInitHook(function() {
  if (this.options.loadingControl) {
    this.loadingControl = new LoadingControlElement();
    this.addControl(this.loadingControl);
  }
});

class LoadingControl extends MapControl {
  static propTypes = {
    delayIndicator: PropTypes.any,
    position: PropTypes.string,
    separate: PropTypes.bool,
    zoomControl: PropTypes.any,
    spinjs: PropTypes.bool,
    spin: PropTypes.shape({
      lines: PropTypes.number.isRequired,
      length: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      radius: PropTypes.number.isRequired,
      rotate: PropTypes.number.isRequired,
      top: PropTypes.string.isRequired
    })
  };

  createLeafletElement(props) {
    return new LoadingControlElement(props);
  }
}

export default withLeaflet(LoadingControl);
